import React, { useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { Button, Input, Table } from "reactstrap";
import swal from "sweetalert";
import moment from "moment";
import { IoArrowBackSharp } from "react-icons/io5";
import { encryptStorage } from "../../../constants/EncryptStorage";
import {
  applyClassToCharacters,
  isUrdu,
  LoadingSpinner,
} from "../../../constants/const";
import { GetLanguageString, NoResult } from "../../helper/Components";
import { GetCustomStyles, GetSelectedLanguage } from "../../helper/Method";
import { ApiFund } from "../ApiFund";
import { viewUsraReceiptPdf } from "../../shared/receiptViewer";
import { useTranslation } from "react-i18next";

const cellStyle = { padding: "14px 16px", color: "#676a6c", verticalAlign: "middle" };
const getHeaderStyle = (isUrduUi) => ({
  padding: "14px 16px",
  color: "#2f4050",
  fontWeight: 700,
  fontSize: 12,
  letterSpacing: isUrduUi ? "0" : "0.3px",
  textTransform: isUrduUi ? "none" : "uppercase",
  background: "#f9fafb",
  borderBottom: "1px solid #e7eaec",
});

const RequestRemittance = ({ history }) => {
  const { t } = useTranslation();
  const isUrduUi = GetSelectedLanguage() === "ur";
  const unitId = parseInt(encryptStorage.getItem("loggedInUnitId"));
  const [holding, setHolding] = useState(null);
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    ApiFund()
      .GetUsraHolding(unitId)
      .then((res) => setHolding(res.data))
      .finally(() => setLoading(false));
  }, [unitId]);

  const receipts = holding?.receipts || [];
  const total = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
  const recipientTitle = holding?.recipientTitle || t("usra_nazim_baitulmaal");
  const headerStyle = getHeaderStyle(isUrduUi);
  const formatAmount = (value) => new Intl.NumberFormat().format(Number(value) || 0);
  const formatDate = (value) => {
    if (!value) return "-";
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : value;
  };

  const submit = () => {
    if (receipts.length === 0) {
      swal({
        title: t("no_receipts_selected"),
        icon: "warning",
        className: isUrduUi ? "ur" : "",
      });
      return;
    }
    setSubmitting(true);
    ApiFund()
      .RequestUsraRemittance({ unitId, fundIds: receipts.map((r) => r.id), note })
      .then((res) => {
        if (res.data.statusCode === "200") {
          swal({
            title: t("usra_remittance_requested"),
            icon: "success",
            className: isUrduUi ? "ur" : "",
          }).then(() => history.push("/funds"));
        } else {
          swal({
            title: t(res.data.message || "usra_remittance_failed"),
            icon: "error",
            className: isUrduUi ? "ur" : "",
          });
        }
      })
      .catch((err) => {
        const msg =
          err.response?.data?.message ||
          err.response?.data?.Message ||
          "usra_remittance_failed";
        swal({
          title: t(msg),
          icon: "error",
          className: isUrduUi ? "ur" : "",
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <div className="wrapper-custom wrapper wrapper-content animated fadeInRight">
      <div className="ibox">
        <div className="ibox-title">
          <h5>
            <GetLanguageString props="usra_forward_cash" />
          </h5>
          <Button
            className={GetCustomStyles().btn_style}
            color="white"
            size="xs"
            onClick={() => history.goBack()}
          >
            <IoArrowBackSharp
              color="#18A689"
              size={15}
              style={{ marginRight: 5, marginBottom: 1 }}
            />
            <GetLanguageString props="courses_participants_back" />
          </Button>
        </div>
        <div className="ibox-content">
          {loading ? (
            LoadingSpinner()
          ) : (
            <>
              <div
                style={{
                  padding: "16px 18px",
                  backgroundColor: "#ffffff",
                  borderRadius: 4,
                  border: "1px solid #e7eaec",
                  borderLeft: "4px solid #1ab394",
                  boxShadow: "0 2px 2px rgba(0,0,0,0.05)",
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    color: "#676a6c",
                    fontWeight: 600,
                    letterSpacing: "0.4px",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ textTransform: isUrduUi ? "none" : "uppercase" }}>
                    <GetLanguageString props="usra_recipient" />
                  </span>
                  {recipientTitle ? (
                    <>
                      {" ("}
                      {applyClassToCharacters(recipientTitle, "ur")}
                      {")"}
                    </>
                  ) : null}
                </div>
                <div style={{ fontSize: 16, fontWeight: 600, color: "#2f4050" }}>
                  {holding?.recipientName
                    ? applyClassToCharacters(holding.recipientName, "ur")
                    : applyClassToCharacters(
                        t("usra_recipient_missing", { title: recipientTitle }),
                        "ur"
                      )}
                </div>
              </div>

              {receipts.length === 0 ? (
                <NoResult />
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <Table
                    responsive
                    className="mb-0"
                    style={{
                      background: "#fff",
                      border: "1px solid #e7eaec",
                      marginBottom: 0,
                    }}
                  >
                    <thead>
                      <tr>
                        <th style={headerStyle}>#</th>
                        <th style={headerStyle}>
                          <GetLanguageString props="funds_receipt_number" />
                        </th>
                        <th style={headerStyle}>
                          <GetLanguageString props="member_report_member_serialNumber" />
                        </th>
                        <th style={headerStyle}>
                          <GetLanguageString props="common_name" />
                        </th>
                        <th style={headerStyle}>
                          <GetLanguageString props="funds_add_fund_amount" />
                        </th>
                        <th style={headerStyle}>
                          <GetLanguageString props="dashboard_event_page_date" />
                        </th>
                        <th style={headerStyle} />
                      </tr>
                    </thead>
                    <tbody>
                      {receipts.map((r, index) => (
                        <tr
                          key={r.id}
                          style={{
                            borderBottom: "1px solid #d9e2e1",
                            background: index % 2 === 0 ? "#ffffff" : "#e6f3ef",
                          }}
                        >
                          <td style={cellStyle}>{index + 1}</td>
                          <td style={cellStyle}>
                            {applyClassToCharacters(r.receiptNumber || "-", "ur")}
                          </td>
                          <td style={cellStyle}>{r.serialNumber || "-"}</td>
                          <td style={cellStyle}>
                            {applyClassToCharacters(r.payerName || "-", "ur")}
                          </td>
                          <td
                            style={{
                              ...cellStyle,
                              fontWeight: 600,
                              color: "#2f4050",
                            }}
                          >
                            {formatAmount(r.amount)}
                          </td>
                          <td style={cellStyle}>{formatDate(r.date)}</td>
                          <td style={{ ...cellStyle, textAlign: isUrduUi ? "left" : "right" }}>
                            {r.receiptToken && (
                              <Button
                                size="sm"
                                color="secondary"
                                outline
                                onClick={() =>
                                  viewUsraReceiptPdf({
                                    receiptToken: r.receiptToken,
                                    t,
                                  })
                                }
                              >
                                <GetLanguageString props="common_view_receipt" />
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot
                      style={{
                        backgroundColor: "#f3f3f4",
                        borderTop: "2px solid #e7eaec",
                      }}
                    >
                      <tr>
                        <td
                          colSpan={4}
                          style={{
                            ...cellStyle,
                            textAlign: isUrduUi ? "left" : "right",
                            color: "#2f4050",
                            fontWeight: 700,
                          }}
                        >
                          <GetLanguageString props="usra_total" />
                        </td>
                        <td
                          style={{
                            ...cellStyle,
                            color: "#1ab394",
                            fontWeight: 700,
                          }}
                        >
                          {formatAmount(total)}
                        </td>
                        <td />
                        <td />
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}

              <div style={{ marginTop: 20, maxWidth: 520 }}>
                <label style={{ fontWeight: 600, color: "#2f4050", marginBottom: 6 }}>
                  <GetLanguageString props="transactions_memo" />
                </label>
                <Input
                  type="textarea"
                  rows={3}
                  className={isUrdu(note) ? "ur no-drag" : "no-drag"}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              <div
                className="text-end"
                style={{ marginTop: 24, display: "flex", justifyContent: "flex-end", gap: 8 }}
              >
                <Button color="white" onClick={() => history.goBack()}>
                  <GetLanguageString props="common_cancel" />
                </Button>
                <Button
                  color="primary"
                  onClick={submit}
                  disabled={!holding?.recipientRafiqId || receipts.length === 0 || submitting}
                >
                  <GetLanguageString props="usra_submit_remittance" />
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default withRouter(RequestRemittance);
